import {CurrencyPipe} from '@angular/common';
import {Component, Input} from '@angular/core';
import {FormBuilder, ReactiveFormsModule} from '@angular/forms';
import {
  EmTranslatePipe,
  FeatureFlagService,
  SupportedLanguage,
  ToastService,
  TranslationService,
} from '@emma-labs/shared/angular';
import {
  CategoryCode,
  Coverage,
  Duration,
  HUMANIA_EMMA_V2_COVERAGE_AMOUNT_INTERVAL,
  HUMANIA_EMMA_V2_MAX_COVERAGE,
  HUMANIA_EMMA_V2_MIN_WHOLE_COVERAGE,
  HUMANIA_EMMA_V2_WHOLE_LIFE_DURATIONS,
  ProviderId,
} from '@emma-labs/shared/typescript';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {TableModule} from 'primeng/table';
import {finalize} from 'rxjs';
import {map} from 'rxjs/operators';
import {ProtectionService} from '../../../../../services/protection.service';
import {CoverageCardComponent} from '../../coverage-card/coverage-card.component';
import {BaseLifeInsuranceCardComponent} from '../base-life-insurance-card.component';

const DEFAULT_SELECTED_DURATION = Duration.P100;

@Component({
  selector: 'em-whole-life-card',
  templateUrl: './whole-life-card.component.html',
  imports: [
    CardModule,
    CoverageCardComponent,
    DropdownModule,
    ReactiveFormsModule,
    CurrencyPipe,
    ProgressSpinnerModule,
    TableModule,
    EmTranslatePipe,
  ],
})
export class WholeLifeCardComponent extends BaseLifeInsuranceCardComponent {
  @Input({required: true}) override set selectedWholeLifeCoverageAmount(coverageAmount: number | undefined) {
    this.form.controls.coverageAmount.setValue(coverageAmount, {emitEvent: false});
    this.setProducts();
  }

  @Input({required: true}) override set selectedTermLifeCoverageAmount(coverageAmount: number | undefined) {
    this.hasDuo = !!coverageAmount;

    this.coverageAmountOptions = this.protectionService.getCoverageDropdownOptions(
      HUMANIA_EMMA_V2_MIN_WHOLE_COVERAGE,
      HUMANIA_EMMA_V2_COVERAGE_AMOUNT_INTERVAL,
      HUMANIA_EMMA_V2_MAX_COVERAGE - (coverageAmount ?? 0),
      this.hasDuo,
    );

    this.setProducts();
  }

  constructor(
    override readonly fb: FormBuilder,
    private readonly toastService: ToastService,
    private readonly translate: TranslationService,
    private readonly protectionService: ProtectionService,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    super(fb, DEFAULT_SELECTED_DURATION);
  }

  setProducts(): void {
    if (
      !this.withProductSelection ||
      !this._ratesVersion ||
      this._smoker == null ||
      this._age == null ||
      this.hasDuo == null ||
      !this._sex ||
      !this.form.controls.coverageAmount.value
    ) {
      return;
    }

    const coverages: Coverage[] = HUMANIA_EMMA_V2_WHOLE_LIFE_DURATIONS.map((duration) => {
      return {lifeInsurances: {[duration]: this.form.controls.coverageAmount.value}};
    });

    this.protectionService
      .getProtections(
        this._ratesVersion!,
        coverages,
        ProviderId.HUMANIA_EMMA_V2,
        [CategoryCode.C1],
        this._age!,
        this._smoker!,
        this._sex!,
        {allowPartialSuccess: true},
      )
      .pipe(
        map((protections) => {
          return protections.map((p, index) => {
            const pi = p.personInsurances[0];
            if (!pi) {
              throw new Error('Person insurance not found');
            }

            if (pi.duration === this.form.controls.duration.value) {
              this.selectedProductIndex = index;
            }

            return {
              name: {
                en: this.translate.getTranslationByLanguage(pi.duration, SupportedLanguage.EN)!,
                fr: this.translate.getTranslationByLanguage(pi.duration, SupportedLanguage.FR)!,
              },
              value: pi.duration,
              price: pi.priceBreakdown.basePremium + pi.priceBreakdown.policyFee,
            };
          });
        }),
        finalize(() => (this.isPriceLoading = false)),
      )
      .subscribe({
        next: (wholeLifeInsurances) => {
          this.products = wholeLifeInsurances;
        },
        error: (_) => {
          this.toastService.error(
            this.translate.currentLanguageIsFr()
              ? `Une erreur est survenue lors du chargement de l'assurance permanente. Veuillez réessayer`
              : `An error occurred while loading the permanent insurance. Please try again`,
          );
        },
      });
  }
}
